import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';

/**
 * SolanaPay
 *
 * Opens a 49 USDC payment on Solana to the OpenBro wallet.
 * Phone: Phantom transfer link. Desktop: payment sheet with QR.
 */

const SOLANA_ADDRESS = 'AsuxGULyZEmRwEwFPCFxq4X4fwxsLTk7fHCTNvkznY3T';
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const USDC_AMOUNT = '49';
const PAYMENT_MESSAGE = 'OpenBro 49 USDC';

const COPY = {
  fr: {
    badge: 'Solana',
    title: 'Paiement Solana',
    amount: '49 USDC',
    note: 'Même abonnement. Le montant est déjà rempli.',
    open: 'Payer avec Phantom',
    scan: 'Ou scanne ce QR avec Phantom.',
    copy: 'Copier l’adresse',
    copied: 'Adresse copiée',
    close: 'Fermer',
  },
  en: {
    badge: 'Solana',
    title: 'Solana payment',
    amount: '49 USDC',
    note: 'Same subscription. The amount is already filled in.',
    open: 'Pay with Phantom',
    scan: 'Or scan this QR with Phantom.',
    copy: 'Copy address',
    copied: 'Address copied',
    close: 'Close',
  },
};

function paymentLinks() {
  const params = new URLSearchParams({
    recipient: SOLANA_ADDRESS,
    amount: USDC_AMOUNT,
    splToken: USDC_MINT,
    label: 'OpenBro',
    message: PAYMENT_MESSAGE,
  });
  return {
    phantomUrl: `https://phantom.app/ul/v1/transfer?${params.toString()}`,
    solanaUrl: `solana:${SOLANA_ADDRESS}?amount=${USDC_AMOUNT}&spl-token=${USDC_MINT}&label=OpenBro&message=${encodeURIComponent(PAYMENT_MESSAGE)}`,
  };
}

function SolanaMark({ gradientId }) {
  return (
    <svg className="v3-sol-mark" viewBox="0 0 397.7 311.7" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#9945FF" />
          <stop offset="100%" stopColor="#14F195" />
        </linearGradient>
      </defs>
      <path fill={`url(#${gradientId})`} d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1l62.7-62.7z" />
      <path fill={`url(#${gradientId})`} d="M64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8z" />
      <path fill={`url(#${gradientId})`} d="M333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.7z" />
    </svg>
  );
}

function shortAddress(address) {
  return `${address.slice(0, 6)}…${address.slice(-6)}`;
}

function SolanaSheet({ lang, onClose }) {
  const copy = COPY[lang] || COPY.fr;
  const { phantomUrl, solanaUrl } = paymentLinks();
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);
  const gradientId = useId().replace(/:/g, '');

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(solanaUrl, {
      margin: 1,
      width: 220,
      errorCorrectionLevel: 'M',
      color: { dark: '#111111', light: '#ffffff' },
    }).then((url) => {
      if (active) setQr(url);
    }).catch(() => {
      if (active) setQr('');
    });
    return () => {
      active = false;
    };
  }, [solanaUrl]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(SOLANA_ADDRESS);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return createPortal(
    <div className="v3-sol-sheet" role="dialog" aria-modal="true" aria-label={copy.title} onClick={onClose}>
      <div className="v3-sol-card" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="v3-sol-x" onClick={onClose} aria-label={copy.close}>×</button>
        <p className="v3-sol-kicker"><SolanaMark gradientId={gradientId} /> Solana</p>
        <h2>{copy.title}</h2>
        <strong>{copy.amount}</strong>
        <p>{copy.note}</p>
        <a className="v3-sol-go" href={phantomUrl}>{copy.open}</a>
        {qr ? <img src={qr} alt="" /> : null}
        <span className="v3-sol-scan">{copy.scan}</span>
        <button type="button" className="v3-sol-addr" onClick={copyAddress}>
          {shortAddress(SOLANA_ADDRESS)}
          <em>{copied ? copy.copied : copy.copy}</em>
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function SolanaPayButton({ lang, className, label }) {
  const copy = COPY[lang] || COPY.fr;
  const [open, setOpen] = useState(false);
  const gradientId = useId().replace(/:/g, '');
  const { phantomUrl } = paymentLinks();

  const openPay = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const phone = window.matchMedia('(max-width: 1099px)').matches;
    if (phone) {
      window.location.assign(phantomUrl);
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button type="button" className={className} onClick={openPay} aria-label={copy.title}>
        <SolanaMark gradientId={gradientId} />
        {label || copy.badge}
      </button>
      {open ? <SolanaSheet lang={lang} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
